// @flow

import React, { memo, useEffect, useState } from 'react'
import type { ComponentType } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { PopupDialog } from '@alphaott/smart-tv-components'
import { APPROVE_BUTTON_FOCUS_KEY } from '@alphaott/smart-tv-components/src/components/Popup/const'
import {
  DialogTitle,
  DialogDescription,
  DialogActions,
  Button,
} from '@alphaott/smart-tv-components/src/components/Popup/elements'
import { useFocusable } from '@alphaott/smart-tv-spatial-navigation'
import { BUTTON_KEY_NAMES } from '@alphaott/smart-tv-platforms'

// Seconds before the retry starts by itself
export const RETRY_COUNTDOWN = 5

// Only this dialog: a compact window whatever the length of the error text.
// Sizes in rem (1rem = 1px of a 1920 wide screen), so they scale with the screen; at most half of it,
// which leaves wide safe fields at the edges. No gap or CSS variables: webOS 3 runs Chrome 38.
// Centred text and symmetric margins work the same way for right-to-left languages
const CompactDialog = styled(PopupDialog)`
  && {
    box-sizing: border-box;
    min-width: 640rem;
    max-width: 960rem;
    padding: 48rem 56rem 40rem;
  }

  ${DialogTitle} {
    max-width: 100%;
    margin-bottom: 24rem;
    text-align: center;
  }

  ${DialogDescription} {
    align-self: stretch;
    min-width: 0;
    margin-bottom: 40rem;
    line-height: 1.4;
    text-align: center;
    word-wrap: break-word;
    overflow-wrap: break-word;
    word-break: break-word;
    /* The error text is usually Latin: in a right-to-left interface its own direction keeps
       the punctuation in place */
    unicode-bidi: -webkit-plaintext;
    unicode-bidi: plaintext;
  }

  ${DialogActions} {
    justify-content: center;
  }

  ${Button} {
    min-width: 280rem;
    margin: 0 12rem;
  }
`

type PlaybackErrorModalProps = {
  // A text of its own, shown as is
  message?: ?string,
  // The engine's message or code, kept literally inside a localized sentence
  code?: ?(string | number),
  onRetry: () => void,
  onClose: () => void,
}

// Mounted only while the playback has failed: unmounting stops the countdown
// Stands for {{error}} while translating. i18next 19 parses the result again, so the engine's text
// is put in only afterwards: braces or $t( in it stay as they are
const ERROR_MARK = '\uE000'

// A code stays as is; an engine's sentence goes in quotes without its own final period,
// since the translated sentence puts one after it
const formatEngineError = (code: string | number): string => {
  const text = String(code).trim()

  return /^\d+$/.test(text) ? text : `"${text.replace(/[.\s]+$/, '')}"`
}

// Only the wording around the engine's message is translated; the message itself never is
// eslint-disable-next-line complexity
export const getErrorDescription = (
  t: Function,
  message: ?string,
  code: ?(string | number),
): string => {
  if (message) return message

  if (code !== undefined && code !== null && code !== '') {
    return t('mwstv_player_stream_error_code', { error: ERROR_MARK })
      .split(ERROR_MARK)
      .join(formatEngineError(code))
  }

  // No known cause: the general text in the language of the app
  return t('mwstv_player_stream_error')
}

const PlaybackErrorModalPure = ({ message, code, onRetry, onClose }: PlaybackErrorModalProps) => {
  const { t } = useTranslation()
  const [countdown, setCountdown] = useState(RETRY_COUNTDOWN)
  // Not focusable itself: gives access to the current focus
  const { getCurrentFocusKey, setFocus } = useFocusable({ focusable: false })
  // Read on the first render: the dialog moves the focus to Retry in its own effect, which runs first
  const [previousFocusKey] = useState(getCurrentFocusKey)

  useEffect(() => {
    const timer = setInterval(() => setCountdown((value) => value - 1), 1000)

    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    if (countdown <= 0) onRetry()
  }, [countdown]) // eslint-disable-line react-hooks/exhaustive-deps

  // Back closes the modal only. Capture phase on window runs before the screen's own Back handlers
  useEffect(() => {
    const handleBackPress = (event) => {
      event.stopPropagation()
      onClose()
    }

    window.addEventListener(BUTTON_KEY_NAMES.BACK, handleBackPress, true)

    return () => window.removeEventListener(BUTTON_KEY_NAMES.BACK, handleBackPress, true)
  }, [onClose])

  // Focus goes back to where it was before the modal
  useEffect(
    () => () => {
      if (previousFocusKey) setFocus(previousFocusKey)
    },
    [], // eslint-disable-line react-hooks/exhaustive-deps
  )

  return (
    <CompactDialog
      isOpen
      focusable
      title={t('mwstv_tv_playback_error')}
      description={getErrorDescription(t, message, code)}
      cancelButtonTitle={t('close')}
      approveButtonTitle={`${t('mwstv_player_retry')} (${Math.max(countdown, 0)})`}
      initialFocusKey={APPROVE_BUTTON_FOCUS_KEY}
      onClose={onClose}
      onApprove={onRetry}
    />
  )
}

export const PlaybackErrorModal: ComponentType<PlaybackErrorModalProps> =
  memo<PlaybackErrorModalProps>(PlaybackErrorModalPure)

export default PlaybackErrorModal
